import { ICommand } from '../../../core/commands/ICommand';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import type { TerminatorType } from '../shapes/flowchart/flowchartShapes.manifest';
import { getTranslatedFlowchartShapeLabel } from '../shapes/flowchart/flowchartShapeI18n';
import { createNodeId, type NodeAddition } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';

export interface CreateTerminatorParams {
  initialStyle?: Readonly<Record<string, unknown>>;
  x: number;
  y: number;
  width?: number;
  height?: number;
  label?: string;
  description?: string;
  terminatorType?: TerminatorType;
}

export class CreateTerminatorCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Terminator';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateTerminatorParams;
  private inner?: CreateShapeCommand;
  private scenarioNodeId: ReturnType<typeof createNodeId>;

  constructor(params: CreateTerminatorParams) {
    this.id = `create-terminator-${Date.now()}`;
    this.timestamp = Date.now();
    this.params = params;
    this.description = 'Create a start/end terminator';
    this.scenarioNodeId = createNodeId(`scenario-${this.id}`);
  }

  execute(): void {
    const attrs: Record<string, unknown> = {
      label: this.params.label ?? getTranslatedFlowchartShapeLabel('start-end'),
      description: this.params.description ?? '',
      terminatorType: this.params.terminatorType ?? 'start',
    };

    this.inner = new CreateShapeCommand({
      initialStyle: this.params.initialStyle,
      type: 'start-end',
      category: 'flowchart',
      x: this.params.x,
      y: this.params.y,
      width: this.params.width,
      height: this.params.height,
      attrs,
    } as any);

    this.inner.execute();
  }

  undo(): void {
    this.inner?.undo();
  }

  redo(): void {
    this.inner?.redo();
  }

  getCreatedId(): string | undefined {
    return this.inner?.getCreatedId?.();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const label = this.params.label ?? getTranslatedFlowchartShapeLabel('start-end');
    const props: Record<string, unknown> = {
      description: this.params.description ?? '',
      terminatorType: this.params.terminatorType ?? 'start',
    };

    const addition: NodeAddition = {
      id: this.scenarioNodeId,
      type: 'start-end',
      name: label,
      position: { x: this.params.x, y: this.params.y },
      props,
    };

    return { additions: [addition] };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      removals: [
        {
          nodeId: this.scenarioNodeId,
          originalData: {
            name: this.params.label ?? getTranslatedFlowchartShapeLabel('start-end'),
            type: 'start-end',
          },
        },
      ],
    };
  }
}
